import type { ActivityKey } from '@/api/dashboard-live';
import { usePilotage } from '../context';
import { BigNumber, DataTable, Panel, Row, SectionState, Unavailable } from '../ui';
import { DailyChart, HourlyBars, Legend, TimeChart } from '../charts';
import { compact, num, time } from '../format';
import { ACTIVITY_LABEL, activityDetail, newUsersDetail, ROLE_LABEL, v1Detail, v2Detail } from '../details';

const ROLES = ['student', 'parent', 'teacher', 'staff'] as const;

/** Écran 2 — Usage V1 / V2 : actifs par rôle, courbes horaires, activité, écoles */
export function UsageScreen() {
  const { payload, palette: p, openDetail } = usePilotage();
  const s = payload.sections;
  const v1 = s.v1?.data;
  const v2 = s.v2?.data;
  const t = s.v2Tenants?.data;

  return (
    <div className="grid h-full min-h-0 grid-cols-1 gap-3 xl:grid-cols-12 xl:grid-rows-[auto_minmax(0,1fr)_minmax(0,1fr)]">
      {/* Actifs par rôle */}
      <Panel title="Actifs aujourd'hui par rôle" subtitle="V2 : année scolaire en cours" className="xl:col-span-7" onOpen={() => openDetail(v2Detail(payload, p))}>
        <div className="grid grid-cols-4 gap-4">
          {ROLES.map((r) => (
            <div key={r} className="space-y-1">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-400">{ROLE_LABEL[r]}</div>
              <div className="flex items-baseline gap-3">
                <BigNumber value={num(t?.activeToday[r])} className="text-3xl" />
                <span className="text-xs text-slate-400">/ {compact(t?.totals[r])}</span>
              </div>
              <div className="flex gap-[2px] overflow-hidden rounded" style={{ height: 8 }}>
                <div style={{ flex: t?.activeToday[r] ?? 0, background: p.v2 }} />
                <div style={{ flex: v1?.activeByRole[r] ?? 0, background: p.v1 }} />
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                V2 {num(t?.activeToday[r])} · V1 {num(v1?.activeByRole[r])}
              </div>
            </div>
          ))}
        </div>
      </Panel>
      <Panel title="Aujourd'hui" className="xl:col-span-5" right={<SectionState section={s.v2} />}>
        <div className="grid grid-cols-2 gap-x-6">
          <Row label="Actifs V2" value={num(v2?.activeUsersToday)} swatch={p.v2} />
          <Row label="Actifs V1" value={num(v1?.activeUsersToday)} swatch={p.v1} />
          <Row label="Connexions V2 (mot de passe)" value={num(v2?.loginsToday)} />
          <Row label="Connexions V1" value={num(v1?.loginsToday)} />
          <Row label="Écoles ouvertes V2" value={num(v2?.schoolsEnteredToday)} />
          <Row label="Écoles actives V1" value={num(v1?.activeSchoolsToday)} />
          <Row label="Comptes V2 créés" value={num(v2?.accountsCreatedToday)} />
          <Row label="Nouveaux élèves / parents V2" value={`${num(t?.newToday.student)} / ${num(t?.newToday.parent)}`} />
        </div>
      </Panel>

      {/* Courbes */}
      <Panel
        title="En ligne (15 dernières minutes)"
        subtitle="24 h glissantes"
        className="xl:col-span-7"
        right={
          <Legend
            defs={[
              { metric: 'v2.online_15m', label: 'V2', color: p.v2 },
              { metric: 'v1.online_15m', label: 'V1', color: p.v1 },
            ]}
          />
        }
      >
        <TimeChart
          defs={[
            { metric: 'v2.online_15m', label: 'V2', color: p.v2 },
            { metric: 'v1.online_15m', label: 'V1', color: p.v1 },
          ]}
          height="100%"
        />
      </Panel>
      <Panel title="Répartition horaire aujourd'hui" className="xl:col-span-5">
        <div className="grid h-full grid-rows-2 gap-2">
          <div className="min-h-0" onClick={() => openDetail(v2Detail(payload, p))}>
            <div className="text-xs text-slate-400">V2 — utilisateurs actifs par heure</div>
            {v2 ? <HourlyBars values={v2.hourly} color={p.v2} height={90} /> : <Unavailable section={s.v2} what="V2" />}
          </div>
          <div className="min-h-0" onClick={() => openDetail(v1Detail(payload, p))}>
            <div className="text-xs text-slate-400">V1 — connexions par heure</div>
            {v1 ? <HourlyBars values={v1.hourly} color={p.v1} height={90} /> : <Unavailable section={s.v1} what="V1" />}
          </div>
        </div>
      </Panel>

      {/* Activité et écoles */}
      <Panel
        title="Activité V2 par jour"
        subtitle="lignes créées dans la journée"
        className="xl:col-span-4"
        onOpen={() => openDetail(activityDetail(payload))}
        right={
          <Legend
            defs={[
              { metric: 'a', label: ACTIVITY_LABEL.attendanceCalls, color: p.v2 },
              { metric: 'g', label: ACTIVITY_LABEL.grades, color: p.v1 },
              { metric: 'm', label: ACTIVITY_LABEL.messages, color: p.third },
            ]}
          />
        }
      >
        <DailyChart
          defs={[
            { metric: 'v2.activity.attendanceCalls', label: ACTIVITY_LABEL.attendanceCalls, color: p.v2 },
            { metric: 'v2.activity.grades', label: ACTIVITY_LABEL.grades, color: p.v1 },
            { metric: 'v2.activity.messages', label: ACTIVITY_LABEL.messages, color: p.third },
          ]}
          days={30}
          height="100%"
        />
      </Panel>
      <Panel title="Activité V2 aujourd'hui" className="xl:col-span-3" right={<SectionState section={s.v2Tenants} />}>
        {t ? (
          <div className="space-y-0.5 overflow-y-auto">
            {(Object.keys(ACTIVITY_LABEL) as ActivityKey[]).map((k) => (
              <button key={k} type="button" className="block w-full text-left hover:bg-slate-50 dark:hover:bg-slate-800" onClick={() => openDetail(activityDetail(payload, k))}>
                <Row label={ACTIVITY_LABEL[k]} value={num(t.activity[k])} />
              </button>
            ))}
          </div>
        ) : (
          <Unavailable section={s.v2Tenants} what="Activité" />
        )}
      </Panel>
      <Panel title="Écoles les plus actives (V2)" className="xl:col-span-5" bodyClassName="overflow-y-auto" onOpen={() => openDetail(newUsersDetail(payload))}>
        <DataTable
          rows={t?.perTenant ?? []}
          max={12}
          columns={[
            { key: 'c', label: 'École', render: (r) => <span title={r.name}>{r.code}</span> },
            { key: 'a', label: 'Actifs', align: 'right', render: (r) => num(r.activeTotal) },
            { key: 's', label: 'Élèves', align: 'right', render: (r) => num(r.active.student) },
            { key: 'p', label: 'Parents', align: 'right', render: (r) => num(r.active.parent) },
            { key: 'ap', label: 'Appels', align: 'right', render: (r) => num(r.activity.attendanceCalls) },
            { key: 'l', label: 'Dernière', align: 'right', render: (r) => time(r.lastConnectionAt) },
          ]}
        />
      </Panel>
    </div>
  );
}
